import React, { useState, useEffect } from "react";
import { supabase } from "../utils/supabaseClient";
import { ToastContainer, toast } from "react-toastify";
import { Image as ImageIcon, Edit2, Trash2 } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import "react-toastify/dist/ReactToastify.css";
import "../styles/IndustriesManagement.css";

const IndustriesManagement = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentIndustry, setCurrentIndustry] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    tagline: "",
    image_url: "",
  });

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const { data, error } = await supabase
        .from("industries")
        .select("*")
        .order("title");

      if (error) throw error;
      setIndustries(data);
    } catch (error) {
      setError(error.message);
      toast.error("Failed to fetch industries");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()
        .toString(36)
        .substring(2)}${Date.now()}.${fileExt}`;
      const filePath = `industries/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("images").getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      throw new Error("Error uploading image: " + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let imageUrl = formData.image_url;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      if (isEditing) {
        const { error } = await supabase
          .from("industries")
          .update({
            title: formData.title,
            tagline: formData.tagline,
            image_url: imageUrl,
          })
          .eq("id", currentIndustry.id);

        if (error) throw error;
        toast.success("Industry updated successfully!");
      } else {
        const { error } = await supabase.from("industries").insert([
          {
            title: formData.title,
            tagline: formData.tagline,
            image_url: imageUrl,
          },
        ]);

        if (error) throw error;
        toast.success("Industry created successfully!");
      }

      setFormData({
        title: "",
        tagline: "",
        image_url: "",
      });
      setImageFile(null);
      setIsEditing(false);
      setCurrentIndustry(null);
      fetchIndustries();
    } catch (error) {
      setError(error.message);
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (industry) => {
    setCurrentIndustry(industry);
    setFormData({
      title: industry.title,
      tagline: industry.tagline,
      image_url: industry.image_url,
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this industry?")) {
      try {
        const { error } = await supabase
          .from("industries")
          .delete()
          .eq("id", id);

        if (error) throw error;
        toast.success("Industry deleted successfully!");
        fetchIndustries();
      } catch (error) {
        setError(error.message);
        toast.error("An error occurred while deleting the industry.");
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      tagline: "",
      image_url: "",
    });
    setImageFile(null);
    setIsEditing(false);
    setCurrentIndustry(null);
  };

  if (loading && !isEditing) return <LoadingSpinner size="large" />;
  if (error)
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );

  return (
    <div className="page-content">
      <ToastContainer position="top-right" />
      <h2>{isEditing ? "Edit Industry" : "Add New Industry"}</h2>

      <form onSubmit={handleSubmit} className="industry-form">
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="tagline">Tagline</label>
          <textarea
            id="tagline"
            name="tagline"
            value={formData.tagline}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="image">Industry Image</label>
          <div className="file-input-container">
            <input
              type="file"
              id="image"
              accept="image/*"
              onChange={handleImageChange}
              className="file-input"
            />
            <label htmlFor="image" className="file-input-label">
              <ImageIcon size={20} />
              <span>{imageFile ? imageFile.name : "Choose an image"}</span>
            </label>
          </div>
          {formData.image_url && !imageFile && (
            <div className="current-image">
              <img src={formData.image_url} alt="Current" />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button">
            {isEditing ? "Update Industry" : "Add Industry"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              className="cancel-button"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="industries-list">
        <h3>Existing Industries</h3>
        <div className="industries-grid">
          {industries.map((industry) => (
            <div key={industry.id} className="industry-card">
              <div className="industry-image">
                <img src={industry.image_url} alt={industry.title} />
              </div>
              <div className="industry-content">
                <h4>{industry.title}</h4>
                <p>{industry.tagline}</p>
                <div className="industry-actions">
                  <button
                    onClick={() => handleEdit(industry)}
                    className="edit-button"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(industry.id)}
                    className="delete-button"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IndustriesManagement;
